/**
 * Max time to wait for a fire-and-forget tracking request (e.g. an SSO signup
 * conversion event) to flush before navigating away. Long enough for the
 * request to leave the browser, short enough that a hung request never strands
 * the user on a spinner. Shared so every call site that flushes before a
 * redirect uses the same budget.
 */
export const TRACKING_FLUSH_TIMEOUT_MS = 1000;

/**
 * Wait for `promise` to settle, but for no longer than `timeoutMs`, and never
 * reject.
 *
 * This is for flushing fire-and-forget work (e.g. an analytics/conversion
 * event) immediately before a navigation that would otherwise cut it off. We
 * want to give the request a chance to leave the browser, but we must never let
 * a slow or hung request strand the user, so the wait is time-boxed and any
 * rejection is swallowed.
 */
export function flushWithin(
  promise: Promise<unknown> | undefined,
  timeoutMs: number
): Promise<void> {
  if (!promise) {
    return Promise.resolve();
  }

  return new Promise<void>(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    void Promise.resolve(promise)
      .catch(() => undefined)
      .then(finish);
  });
}
